import React from 'react';
import { Card, CardContent, Grid, Box, Typography } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { SHARED_TYPOGRAPHY } from '../../../theme';

/**
 * QuickInsights Component
 *
 * A card component that displays key insights about templates, costs, and performance.
 * Uses the design system for consistent styling.
 *
 * @param {object} overviewData - Overview data containing success rate
 * @param {object} templatePerformance - Template performance data
 * @param {object} costBreakdown - Cost breakdown data
 */
const QuickInsights = ({
  overviewData,
  templatePerformance,
  costBreakdown,
}) => {
  if (!overviewData || !templatePerformance || !costBreakdown) return null;

  return (
    <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={SHARED_TYPOGRAPHY.h4Bold}>
            Quick Insights
          </Typography>
        </Box>

        {/* Insights Grid */}
        <Grid container spacing={2}>
          {/* Templates Available */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="primary" />
              <Typography variant="body1" sx={SHARED_TYPOGRAPHY.bodyMedium}>
                {overviewData.total_templates > 0
                  ? `${overviewData.total_templates} templates available`
                  : 'No templates available'}
              </Typography>
            </Box>
          </Grid>

          {/* Most Expensive Model */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOnIcon color="warning" />
              <Typography variant="body1" sx={SHARED_TYPOGRAPHY.bodyMedium}>
                {costBreakdown.models && costBreakdown.models.length > 0
                  ? `Most expensive model: ${costBreakdown.models[0].model_name} ($${(costBreakdown.models[0].total_cost || 0).toFixed(2)})`
                  : 'No cost data available'}
              </Typography>
            </Box>
          </Grid>

          {/* System Performance */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="body1" sx={SHARED_TYPOGRAPHY.bodyMedium}>
                {(overviewData.success_rate || 0) >= 95
                  ? 'Excellent system performance!'
                  : (overviewData.success_rate || 0) >= 90
                    ? 'Good system performance'
                    : 'System performance needs attention'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QuickInsights;
